import { ArrowDown, ArrowUp } from 'lucide-react';
import type React from 'react';
import { Card } from '../tailgrids/core/card.js';
import { cn } from '../../utils/cn.js';

export type StatTone = 'accent' | 'success' | 'warning' | 'danger' | 'purple' | 'neutral';

export const STAT_TONE_TILE: Record<StatTone, string> = {
  accent: 'bg-accent-muted text-accent-text',
  success: 'bg-success-muted text-success',
  warning: 'bg-warning-muted text-warning',
  danger: 'bg-danger-muted text-danger',
  purple: 'bg-purple-muted text-purple',
  neutral: 'bg-surface-alt text-muted',
};

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  /** Percent change against the previous period; positive renders green with an up arrow. */
  change?: number;
  prefix?: string;
  suffix?: string;
  icon?: React.ReactNode;
  /** Colour of the icon tile; draw attention (`danger`, `warning`) only when the number needs action. */
  tone?: StatTone;
  /** A second line under the label (e.g. "3 failing"). */
  sub?: React.ReactNode;
  className?: string;
}

/** One headline number (NextAdmin overview-stats card): icon tile, value, label and optional trend. */
export function StatCard({
  label,
  value,
  change,
  prefix = '',
  suffix = '',
  icon,
  tone = 'accent',
  sub,
  className,
}: StatCardProps) {
  const positive = change !== undefined && change >= 0;
  return (
    <Card className={cn('flex flex-col gap-4 p-5', className)}>
      {icon && (
        <div
          className={cn('flex size-8 items-center justify-center rounded-lg [&>svg]:size-4.5', STAT_TONE_TILE[tone])}
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate text-xl leading-7 font-semibold text-heading tabular-nums md:text-2xl md:leading-8">
          {prefix}
          {typeof value === 'number' ? value.toLocaleString() : value}
          {suffix}
        </div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="truncate text-sm leading-5 font-medium text-muted">{label}</span>
          {change !== undefined && (
            <span
              className={cn(
                'flex shrink-0 items-center gap-0.5 text-sm leading-5 font-medium tabular-nums',
                positive ? 'text-success' : 'text-danger',
              )}
            >
              {positive ? '+' : ''}
              {change.toFixed(2)}%
              {positive ? <ArrowUp size={14} aria-hidden="true" /> : <ArrowDown size={14} aria-hidden="true" />}
            </span>
          )}
        </div>
        {sub != null && <div className="mt-1 text-xs text-muted">{sub}</div>}
      </div>
    </Card>
  );
}
